import axios from "axios";
import errorHandler from "@/services/errorHandler";
import sessionService from "@/services/session";
import { SessionExpiredError, isAuthExpiredPayload } from "@/services/sessionExpiredError";
import { useAuthStore } from "@/stores/auth";

function sesionCaducada(cause) {
  // Sin await: el aviso de sesión caducada espera al usuario y la petición
  // no debe quedarse colgada mientras tanto.
  sessionService.handleTokenExpiry().catch(() => {});
  return Promise.reject(new SessionExpiredError(cause));
}

// Token rechazado: se renueva con el refresh token (si nadie lo ha hecho ya)
// y se reintenta la petición una vez. Un 402 implica que el backend no la
// ejecutó, así que reintentar es seguro. El token de IndoManager no tiene
// refresh: sigue yendo directo al login.
async function renovarYReintentar(config, cause) {
  const auth = useAuthStore();
  const enviado = config?.headers?.Authorization;
  if (
    !enviado ||
    config._reintentada ||
    !auth.refreshToken ||
    enviado === `Bearer ${auth.tokenManager}`
  ) {
    return sesionCaducada(cause);
  }
  try {
    if (enviado === `Bearer ${auth.token}`) await auth.RenovarToken();
  } catch {
    return sesionCaducada(cause);
  }
  config._reintentada = true;
  config.headers.Authorization = `Bearer ${auth.token}`;
  return axios.request(config);
}

export default {
  indonetURL: import.meta.env.VITE_APP_INDONET_URL,
  indoscanURL: import.meta.env.VITE_APP_INDOSCAN_URL,
  moodleRestURL: import.meta.env.VITE_APP_MOODLE_REST_URL,
  moodleURL: import.meta.env.VITE_APP_MOODLE_URL,
  ofertasURL: import.meta.env.VITE_APP_OFERTAS_URL,
  indobiselURL: import.meta.env.VITE_APP_BISELADO_URL,
  moodleToken: import.meta.env.VITE_APP_MOODLE_TOKEN,
  indoboxURL: import.meta.env.VITE_APP_INDOBOX_URL,
  // En el dev server va por el proxy de vite.config.js (la API aún no tiene CORS)
  cnxtApiURL: import.meta.env.DEV ? "/cnxt-api/" : import.meta.env.VITE_APP_CNXT_API,
  headers: {
    "Content-Type": "text/xml; charset=utf-8",
    Accept: "text/xml; charset=utf-8",
  },
  headersOfertas: {
    Accept: "text/json; charset=utf-8",
  },

  setupInterceptors() {
    // Response interceptor para manejar errores de autenticación Y errores de servidor/conexión
    axios.interceptors.response.use(
      (response) => {
        // Un 402 dentro de una respuesta 200 significa que el backend ha
        // rechazado el token sin ejecutar la operación. Se renueva y
        // reintenta; si no se puede, se cierra sesión y se rechaza para que
        // ningún caller procese la respuesta como válida.
        if (!response.config?.skipAuth && isAuthExpiredPayload(response.data)) {
          return renovarYReintentar(response.config, response);
        }
        return response;
      },
      async (error) => {
        // El interceptor sólo actúa sobre errores reales del backend:
        //   - 5xx → modal de error del servidor (vía errorHandler)
        //   - 401 / 403 / 402 en payload → renovar token y reintentar; si no
        //     se puede, handleTokenExpiry (aviso y login)
        // En los casos de sesión se rechaza con SessionExpiredError para que
        // los callers puedan ignorarlo (la redirección al login ya está en marcha).
        // Cualquier otra cosa (sin response, 4xx genéricos, aborts, timeouts)
        // se propaga al caller sin tocar el modal global.

        const status = error.response?.status;

        if (status >= 500 && !error.config?.silentError) {
          errorHandler.handleServerError(error, status);
        }

        if (
          !error.config?.skipAuth &&
          (status === 401 || status === 403 || isAuthExpiredPayload(error.response?.data))
        ) {
          return renovarYReintentar(error.config, error);
        }

        return Promise.reject(error);
      },
    );

    // Request interceptor para asegurar que el token esté siempre presente
    axios.interceptors.request.use(
      (config) => {
        // Login / RenovarToken / CerrarSesion: nunca con un token (quizá
        // caducado), tampoco el que llega de axios.defaults.
        if (config.skipAuth) {
          if (config.headers?.delete) config.headers.delete("Authorization");
          else if (config.headers) delete config.headers.Authorization;
          return config;
        }

        const authStore = useAuthStore();
        const token = authStore.token || authStore.tokenManager;

        // Lista de endpoints que NO deben llevar Authorization
        const noAuthEndpoints = [
          "/ws/service1.asmx", // pon aquí los endpoints públicos o problemáticos
          "/ws/otro-endpoint-publico",
        ];

        // Si la URL incluye alguno de los endpoints de la lista, NO añadas Authorization
        const needsNoAuth = noAuthEndpoints.some((endpoint) => config.url.includes(endpoint));

        if (token && !config.headers.Authorization && !needsNoAuth) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error),
    );
  },
};
